/* eslint-disable no-nested-ternary */
/* eslint-disable no-return-assign */
import * as Yup from 'yup';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm, Controller, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// auth action

// @mui
import { Stack, IconButton, InputAdornment, Box, Typography, Button, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { sendOTP, verifyResetOTP, resetPassword } from '../../../store/actions/auth';

// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField } from '../../../components/hook-form';

// --- Styled Components ---

const AppleTextField = styled(RHFTextField)({
  '& .MuiOutlinedInput-root': {
    fontFamily: '"SF Pro Text", -apple-system, sans-serif',
    borderRadius: 12,
    background: '#f5f5f7',
    border: '1.5px solid #d2d2d7',
    '& fieldset': { border: '1.5px solid transparent' },
    '&.Mui-focused': {
      background: '#ffffff',
      boxShadow: '0 0 0 4px rgba(0, 122, 255, 0.12)',
      '& fieldset': { borderColor: '#0071e3' },
    },
  },
});

const SubmitButton = styled(LoadingButton)(({ theme, disabled }) => ({
  borderRadius: 12,
  height: 50,
  textTransform: 'none',
  color: 'white',
  fontWeight: 590,
  background: disabled ? '#bcbcc5' : 'linear-gradient(180deg, #1c1c1e 0%, #000000 100%)',
  '&:hover': { background: disabled ? '#bcbcc5' : '#2c2c2e' },
  boxShadow: 'none',
  '&.Mui-disabled': {
    color: 'white',
    background: '#bcbcc5',
  },
}));

// --- Custom Components ---

const OTPInput = ({ name }) => {
  const { control } = useFormContext();
  const inputRefs = useRef([]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const otpValues = (value || '').split('').concat(Array(6).fill('')).slice(0, 6);

        const handleChange = (e, index) => {
          const val = e.target.value;
          if (/[^0-9]/.test(val)) return;

          const newOtp = [...otpValues];
          newOtp[index] = val.substring(val.length - 1);

          onChange(newOtp.join(''));

          if (val && index < 5) {
            inputRefs.current[index + 1]?.focus();
          }
        };

        const handleKeyDown = (e, index) => {
          if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
          }
        };

        const handlePaste = (e) => {
          e.preventDefault();
          const pasteData = e.clipboardData
            .getData('text')
            .replace(/[^0-9]/g, '')
            .substring(0, 6);
          if (pasteData) {
            onChange(pasteData);
            const focusIndex = Math.min(pasteData.length, 5);
            if (inputRefs.current[focusIndex]) {
              inputRefs.current[focusIndex].focus();
            } else if (inputRefs.current[5]) {
              inputRefs.current[5].focus();
            }
          }
        };

        return (
          <Box>
            <Typography sx={{ mb: 1, color: '#6e6e73', fontWeight: 500, fontSize: '0.875rem' }}>OTP</Typography>
            <Stack direction="row" spacing={1} justifyContent="space-between">
              {otpValues.map((digit, index) => (
                <Box
                  key={index}
                  component="input"
                  ref={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  sx={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '6px',
                    border: '1.5px solid #d2d2d7',
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontFamily: '"SF Pro Text", -apple-system, sans-serif',
                    background: '#ffffff',
                    outline: 'none',
                    '&:focus': {
                      borderColor: '#0071e3',
                      boxShadow: '0 0 0 4px rgba(0, 122, 255, 0.12)',
                    },
                  }}
                />
              ))}
            </Stack>
            {error && <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>{error.message}</Typography>}
          </Box>
        );
      }}
    />
  );
};

// ----------------------------------------------------------------------

export default function PasswordResetForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [step, setStep] = useState(1); // 1: email, 2: otp, 3: new password
  const [showPassword, setShowPassword] = useState(false);
  const [countdown, setCountdown] = useState(36);

  const ResetSchema = Yup.object().shape({
    email: Yup.string().email('Must be a valid email').required('Email is required'),
    otp: step >= 2 ? Yup.string().length(6, 'OTP must be 6 digits').required('OTP is required') : Yup.string(),
    password: step === 3 ? Yup.string().required('New password is required') : Yup.string(),
  });

  const methods = useForm({
    resolver: yupResolver(ResetSchema),
    defaultValues: { email: '', otp: '', password: '' },
  });

  const { handleSubmit, watch } = methods;

  const currentEmail = watch('email');
  const currentOtp = watch('otp');

  const onSubmit = async (data) => {
    if (step === 1) {
      const success = await dispatch(sendOTP(data.email));
      if (success) {
        setStep(2);
        setCountdown(36);
      }
    } else if (step === 2) {
      const success = await dispatch(verifyResetOTP(data.email, data.otp));
      if (success) {
        setStep(3);
      }
    } else if (step === 3) {
      const success = await dispatch(resetPassword(data.email, data.otp, data.password));
      if (success) {
        navigate('/login');
      }
    }
  };

  useEffect(() => {
    let timer;
    if (step === 2 && countdown > 0) {
      timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [step, countdown]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {step === 1 && (
          <>
            <Typography sx={{ color: '#6e6e73', fontSize: '0.9rem', mb: 1, textAlign: 'center' }}>
              Enter the email address associated with your account and we'll send you an OTP to reset your password.
            </Typography>
            <AppleTextField name="email" label="Email Address" autoFocus />
          </>
        )}

        {step === 2 && (
          <>
            <Box>
              <Typography sx={{ color: '#6e6e73', fontSize: '0.9rem', mb: 0.5 }}>Enter the code sent to:</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ color: '#1d1d1f', fontWeight: 600, fontSize: '0.9rem' }}>{currentEmail}</Typography>
                <IconButton size="small" onClick={() => setStep(1)} sx={{ color: '#6e6e73' }}>
                  <Iconify icon="eva:edit-2-outline" width={16} />
                </IconButton>
              </Stack>
            </Box>

            <OTPInput name="otp" />

            <Typography sx={{ textAlign: 'center', color: '#6e6e73', fontSize: '0.875rem' }}>
              {countdown > 0 ? (
                `Resend OTP in ${countdown} seconds`
              ) : (
                <Link
                  component="button"
                  variant="body2"
                  onClick={async (e) => {
                    e.preventDefault();
                    const success = await dispatch(sendOTP(currentEmail));
                    if (success) setCountdown(36);
                  }}
                  sx={{ color: '#0071e3', textDecoration: 'none' }}
                >
                  Resend OTP
                </Link>
              )}
            </Typography>
          </>
        )}

        {step === 3 && (
          <>
            <Typography sx={{ color: '#6e6e73', fontSize: '0.9rem', mb: 1, textAlign: 'center' }}>
              OTP verified. Please enter your new password.
            </Typography>
            <AppleTextField
              name="password"
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </>
        )}

        <SubmitButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          disabled={step === 2 && (!currentOtp || currentOtp.length !== 6)}
        >
          {step === 1 ? 'Send OTP' : step === 2 ? 'Verify OTP' : 'Reset Password'}
        </SubmitButton>

        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ mt: 2 }}>
          <Link
            component="button"
            onClick={(e) => {
              e.preventDefault();
              navigate('/login');
            }}
            sx={{
              color: '#0071e3',
              fontSize: '0.875rem',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Back to login
          </Link>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
